<template>
  <v-container>
    <v-layout>
      <v-flex xs12 id="messages">
          <Alert
            v-if="showConfirmation"
            class="test-update-confirm"
            type="success"
            @close="clearConfirmation">
            Your account information has been updated
          </Alert>

          <Alert v-if="showFormErrors" type="error" @close="clearFormErrors">
            <div v-for="error in errors.items" :key="error.id">
              <span v-if="error.msg">{{error.msg}}</span>
            </div>
          </Alert>

          <Alert v-if="showApiErrors" type="error" @close="clearApiErrors">
            <div v-for="(errorMessage, index) in hgApiErrorMessages" :key="`api_error_${index}`">
              {{errorMessage}}
            </div>
          </Alert>

        <div class="sub-header">
          <h2>Update your information</h2>

          <v-flex md8 offset-md2 xs12>
            <span class="header-subtext">
              Please update your account information below.
              Required fields are indicated with an asterisk.
              Be sure to click "submit" when you are finished.
            </span>
          </v-flex>

          <div class="last-updated-container">
            <img
              alt='info-icon'
              src="../../assets/icons/grey-info.svg"
              height='14px'
              width="14px"
              contain
              class="info-img"/>
            <div>
              Last updated  <b>{{ lastModifiedTime }}</b>
              <span v-if="accountLastModifiedBy">&nbsp;by</span> <b>{{ accountLastModifiedBy }}</b>
            </div>
          </div>
          <div class="required-text">
            * = required
          </div>
        </div>
      </v-flex>
    </v-layout>

    <form @submit.prevent="validateAndSubmit" novalidate>
      <v-layout row wrap>
        <v-flex xs12 md6>
          <div class="input-holder">
            <TextInput
              v-model="model.username"
              :placeholder="getPlaceholder('userName')"
              :validations="inputValidations.userName"
              display-name="User Name*"
              identifier="userName" />

            <div class="username-text">
              Pick a User Name that is easy for you to remember.
              No spaces are allowed.
            </div>
          </div>
        </v-flex>

        <v-flex xs12 md6 align-content-start>
          <div class="input-holder password-container">
            <label for="password">Password*</label>
            <PasswordValidationField
                v-model="model.password"
                :validate="{ confirmed: model.password2 }"
                autocomplete="new-password"
                id="password"
                data-vv-name="Password"
                name="password"
                flat
                solo />
          </div>

          <div class="input-holder password-container">
            <label for="password-confirm">Confirm Password*</label>
            <PasswordField
              v-model="model.password2"
              v-validate="inputValidations.password"
              autocomplete="new-password"
              id="password-confirm"
              name="password-confirm"
              data-vv-name="Confirm Password"
              flat
              solo />
          </div>
        </v-flex>

        <v-flex md6 xs12>
          <div class="input-holder">
            <TextInput
              v-model="model.givenName"
              :placeholder="getPlaceholder('firstName')"
              :validations="inputValidations.firstName"
              display-name="First Name"
              identifier="givenName" />
          </div>
        </v-flex>

        <v-flex md6 xs12>
          <div class="input-holder">
            <TextInput
              v-model="model.familyName"
              :placeholder="getPlaceholder('lastName')"
              :validations="inputValidations.lastName"
              display-name="Last Name"
              identifier="familyName" />
          </div>
        </v-flex>

        <v-flex xs12>
          <div class="input-holder">
            <div class="full-input">
              <TextInput
                v-model="model.primaryEmail"
                :placeholder="getPlaceholder('email')"
                :validations="inputValidations.email"
                display-name="Email*"
                id="email"
                type="email" />
            </div>
          </div>
        </v-flex>

        <v-flex xs12>
          <div class="input-holder">
            <div class="full-input">
              <TextInput
                v-model="model.primaryAddress.address1"
                :placeholder="getPlaceholder('address1')"
                :validations="inputValidations.address1"
                display-name="Street Address"
                identifier="addressLine1" />
            </div>
          </div>
        </v-flex>

        <v-flex xs12>
          <div class="input-holder">
            <div class="full-input">
              <TextInput
                v-model="model.primaryAddress.address2"
                :placeholder="getPlaceholder('address2')"
                :validations="inputValidations.address2"
                display-name="Address 2"
                identifier="addressLine2" />
            </div>
          </div>
        </v-flex>

        <v-flex xs12 md6>
          <div class="input-holder">
            <TextInput
              v-model="model.primaryAddress.city"
              :placeholder="getPlaceholder('city')"
              :validations="inputValidations.city"
              display-name="City" />
          </div>
        </v-flex>

        <v-flex xs12 md4>
          <div class="input-holder">
            <TextInput
              v-model="model.primaryAddress.state"
              :placeholder="getPlaceholder('state')"
              :validations="{ ...inputValidations.state, max: 3, required: true }"
              display-name="State"
              maxlength="3" />
          </div>
        </v-flex>

        <v-flex xs12 md2>
          <div class="input-holder">
            <TextInput
              v-model="model.primaryAddress.postalCode"
              :placeholder="getPlaceholder('postalCode')"
              :validations="inputValidations.postalCode"
              display-name="Zip/Postal Code"
              identifier="zip" />
          </div>
        </v-flex>

        <v-flex md6 xs12>
          <div class="input-holder">
            <TextInput
              v-model="model.homePhone"
              :placeholder="getPlaceholder('phone')"
              :validations="{ ...inputValidations.phone, min: 10 }"
              display-name="Home Phone"
              mask="###-###-####" />
          </div>
        </v-flex>
        <v-flex md6 xs12>
          <div class="input-holder">
            <TextInput
              v-model="model.workPhone"
              :placeholder="getPlaceholder('phone')"
              :validations="{ ...inputValidations.phone, min: 10 }"
              display-name="Work Phone"
              mask="###-###-####" />
          </div>
        </v-flex>

        <v-flex md6 xs12>
          <div class="input-holder">
            <TextInput
              v-model="model.mobilePhone"
              :placeholder="getPlaceholder('phone')"
              :validations="{ ...inputValidations.phone, min: 10 }"
              display-name="Mobile Phone"
              mask="###-###-####" />
          </div>
        </v-flex>

        <v-flex xs12 align-center>
          <div class="input-holder">
            <button type="submit" class="btn-primary btn-submit" :disabled="hgApiLoading">
              {{submitButtonLabel}}
            </button>
          </div>
        </v-flex>
      </v-layout>
    </form>
  </v-container>
</template>

<script>
import { Components, Constants } from '@homegauge/vue';

import Alert from '@/components/Shared/Alert.vue';
import TextInput from '@/components/Shared/TextInput.vue';
import Moment from 'moment';

const { PasswordField, PasswordValidationField } = Components;
const { InputPlaceholders, InputValidations } = Constants;

const defaultModel = {
  password: '',
  password2: '',
  primaryAddress: {},
};

export default {
  name: 'hgAccountUpdate',
  components: {
    Alert,
    PasswordField,
    PasswordValidationField,
    TextInput,
  },
  data() {
    return {
      showConfirmation: false,
      showErrors: false,
      inputValidations: InputValidations,
      model: defaultModel,
    };
  },
  props: {
    hgApiErrorMessages: {
      type: Array,
      default: () => [],
    },
    hgApiLoading: {
      type: Boolean,
      default: false,
    },
    hgCustomer: {
      type: Object,
      required: true,
    },
    hgAccountLastModifiedResponse: {
      type: Object,
      required: true,
    },
  },
  computed: {
    passwordCheck() {
      return (this.model.password !== '' && this.model.password2 !== '');
    },
    showApiErrors() {
      return this.hgApiErrorMessages.length > 0;
    },
    showFormErrors() {
      return this.errors && this.errors.items.length > 0;
    },
    submitButtonLabel() {
      return this.hgApiLoading ? 'Saving' : 'Submit';
    },
    isPhoneRequired() {
      const { homePhone, mobilePhone, workPhone } = this.model;
      return !!(!homePhone.trim()
        && !mobilePhone.trim()
        && !workPhone.trim());
    },
    lastModifiedTime() {
      if (!this.hgAccountLastModifiedResponse.accountLastModifiedTime) {
        return '';
      }
      const date = this.hgAccountLastModifiedResponse.accountLastModifiedTime;
      return Moment(Moment(date)
        .add(
          Moment(date).utcOffset(),
          'minutes',
        )).format('MMM DD, YYYY');
    },
    accountLastModifiedBy() {
      return this.hgAccountLastModifiedResponse.accountLastModifiedByAdmin ? 'Admin' : this.toCamelCase(this.hgAccountLastModifiedResponse.accountLastModifiedByType);
    },
  },
  watch: {
    hgApiErrorMessages(newVal) {
      if (newVal.length > 0) {
        this.$scrollTo('#messages');
      }
    },
  },
  mounted() {
    this.$set(this, 'model', { ...defaultModel, ...this.hgCustomer });
  },
  provide() {
    return {
      $validator: this.$validator,
    };
  },
  methods: {
    clearApiErrors() {
      this.$emit('clearApiErrors');
    },
    clearFormErrors() {
      this.errors.clear();
    },
    clearConfirmation() {
      this.showConfirmation = false;
    },
    getPlaceholder(placeholderConstant) {
      return InputPlaceholders[placeholderConstant]
        ? `ie: ${InputPlaceholders[placeholderConstant]}`
        : '';
    },
    validatePhoneNumbers() {
      const phoneNumberErrorKey = 'onePhoneNumberRequired';
      this.errors.remove(phoneNumberErrorKey);
      if (this.isPhoneRequired) {
        this.errors.add({
          field: phoneNumberErrorKey,
          msg: 'At least one Phone Number is required',
        });
        this.$validator.flag('home-phone', { valid: false });
        this.$validator.flag('mobile-phone', { valid: false });
        this.$validator.flag('work-phone', { valid: false });
      }
    },
    async validateAndSubmit() {
      this.clearFormErrors();
      this.showConfirmation = false;

      await this.$validator.validateAll();
      this.validatePhoneNumbers();

      if (this.errors.any()) {
        this.$scrollTo('#messages');
      } else {
        this.submitPassword();
      }
    },
    async submitMyAccount() {
      const customer = JSON.parse(JSON.stringify(this.model));
      delete customer.password;
      delete customer.password2;

      this.$store.dispatch('User/saveMyCustomer', customer)
        .then(() => {
          this.showConfirmation = true;
          this.$emit('userUpdated', this.model);
          this.$scrollTo('#messages');
        })
        .catch(() => {
          // TODO: Show error when error
        });
    },
    submitPassword() {
      if (this.passwordCheck) {
        this.$store.dispatch('Customer/savePassword', {
          personId: this.hgCustomer.personId,
          password: this.model.password,
        })
          .then(() => {
            this.model.password = '';
            this.model.password2 = '';
            this.submitMyAccount();
          })
          .catch(() => {
            // TODO: Show error when error
          });
      } else {
        this.submitMyAccount();
      }
    },
    toCamelCase(inputText) {
      if (!inputText) {
        return '';
      }
      const tempStr = inputText.toLowerCase();
      return tempStr.substr(0, 1).toUpperCase() + tempStr.substr(1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/breakpoints';
@import '../../styles/vars/typography';

.input-holder {
  padding: 5px 15px;
}

.btn-submit {
  display: block;
  margin: 30px auto 0;
  text-transform: uppercase;

  &:disabled {
    cursor: not-allowed;
  }
}

h2 {
  font-size: 2.143rem;
  padding-bottom: 20px;
}

.sub-header {
  font-size: 1.286rem;
  text-align: center;
}

.zip {
  white-space: nowrap;
}

.header-subtext {
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  width: 525px;
}

.required-text {
  font-weight: bold;
  padding: 10px;
}

.username-text {
  color: rgba(0, 0, 0, 0.6);
}

.field-spacer {
  padding-top: 40px;
}

.password-container {
  ::v-deep .v-input {
    border: solid 1px rgba(0,0,0, 0.15);
    border-radius: 5px;
    margin: 5px 1px 20px 0;
    overflow: hidden;
    padding: 0;
  }

  ::v-deep .v-input--is-focused {
    box-shadow: 0 0 1px  $theme-color-primary;
    border: 1px solid $theme-color-primary;
  }

  ::v-deep .v-text-field.v-text-field--solo .v-input__control {
    height: 42px;
    min-height: 42px;
  }

  ::v-deep .v-text-field__details {
    display: none;
  }

  ::v-deep .v-input__slot {
    margin: 0;
  }

  ::v-deep input {
    border: none;
    color: rgba(0, 0, 0, 0.7);
    font-size: 1rem;
    margin: 0;
    padding: 10px 15px;

    &:focus {
      border: none;
      box-shadow: none;
      outline: none;
    }
  }
}

.last-updated-container {
  display: flex;
  justify-content: center;
  margin: 16px 0px 6px 0px;
  padding: 8px;
  background-color: #f5f6f8;
  font-family: $typography-font-primary;
  font-size: 14px;
  line-height: 1.43;
  color: #666;
  @include breakpoint(sm) {
    align-items: center;
  }
  & .info-img {
    margin: 4px 10px 0px 0px;
    @include breakpoint(sm) {
      margin: 0px 10px 0px 0px;
    }
  }
}

@media all and (max-width: 655px) {
  .field-spacer {
    padding-top: 5px;
  }

  .layout {
    flex-direction: column;
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
